import { useState, useEffect } from "react";
import { Menu, X, Phone, Palette, MapPin, ShieldCheck, HelpCircle, Star, ChevronRight, ExternalLink, Mail, Calculator, CalendarCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import logoWide from "@/assets/logo-wide.webp";

const LIGHT_HERO_PAGES = ["/contact", "/maintenance"];

const navLinks = [
  { label: "Instant Quote", href: "/instant-quote", isRoute: true },
  { label: "Screen Color Options", href: "/screen-options", isRoute: true },
  { label: "Areas", href: "/areas", isRoute: true },
  { label: "Warranty", href: "/warranty", isRoute: true },
  { label: "FAQ", href: "/#faq", isRoute: true },
  { label: "Reviews", href: "https://maps.app.goo.gl/kRXUJi8YucuitEr88", isExternal: true },
];

const mobileNavGroups = [
  {
    label: "Explore",
    items: [
      { label: "Instant Quote", href: "/instant-quote", icon: Calculator, isRoute: true },
      { label: "Screen Color Options", href: "/screen-options", icon: Palette, isRoute: true },
      { label: "Areas We Serve", href: "/areas", icon: MapPin, isRoute: true },
      { label: "Warranty", href: "/warranty", icon: ShieldCheck, isRoute: true },
      { label: "FAQ", href: "/#faq", icon: HelpCircle, isRoute: true },
      { label: "Reviews", href: "https://maps.app.goo.gl/kRXUJi8YucuitEr88", icon: Star, isExternal: true },
    ],
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isLightPage = LIGHT_HERO_PAGES.includes(location.pathname);
  const useDarkText = menuOpen;

  const closeMenuForNavigation = () => {
    document.body.dataset.skipMenuRestore = "true";
    setMenuOpen(false);
  };

  useEffect(() => {
    let ticking = false;
    let lastScrolled = false;
    let pendingScrollY = 0;
    const update = () => {
      // Read the cached scrollY captured at event time, not inside the
      // rAF — avoids a layout read after style writes from other
      // listeners (parallax CSS variable, etc.) that would force reflow.
      const next = pendingScrollY > 20;
      if (next !== lastScrolled) {
        lastScrolled = next;
        setScrolled(next);
      }
      ticking = false;
    };
    const onScroll = () => {
      pendingScrollY = window.scrollY;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (!menuOpen) return;
    const { body } = document;
    const scrollY = window.scrollY;
    const prevOverflow = body.style.overflow;
    const prevPosition = body.style.position;
    const prevTop = body.style.top;
    const prevWidth = body.style.width;

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.dataset.menuScrollY = String(scrollY);

    return () => {
      const restoredScrollY = Number(body.dataset.menuScrollY ?? scrollY);
      const shouldSkipRestore = body.dataset.skipMenuRestore === "true";
      delete body.dataset.menuScrollY;
      delete body.dataset.skipMenuRestore;
      body.style.overflow = prevOverflow;
      body.style.position = prevPosition;
      body.style.top = prevTop;
      body.style.width = prevWidth;

      if (!shouldSkipRestore && window.location.pathname === location.pathname) {
        window.scrollTo({ top: restoredScrollY, left: 0, behavior: "auto" });
      }
    };
  }, [menuOpen, location.pathname]);

  // Close menu on route change and scroll to top (unless navigating to a hash)
  useEffect(() => {
    setMenuOpen(false);
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <header
      className={`fixed safe-top-header left-0 right-0 z-50 transition-all duration-500 ${
        menuOpen ? "bg-card shadow-fun py-1" : "bg-transparent py-2"
      }`}
    >
      <div
        className={`container flex items-center justify-between overflow-visible transition-[height] duration-500 ease-out ${
          scrolled ? "h-[4.5rem] md:h-[5.5rem]" : "h-[7.2rem] sm:h-[7.2rem] md:h-32 lg:h-36"
        }`}
      >
        {/* Logo — grows when at top, shrinks on scroll */}
        <Link to="/" className="flex items-center relative z-10 shrink-0">
          <img
            src={logoWide}
            alt="Valley Sun Screens"
            className={`w-auto object-contain drop-shadow-md transition-[height] duration-500 ease-out ${
              scrolled
                ? "h-[3.95rem] md:h-[4.5rem]"
                : "h-[7.2rem] sm:h-[7.2rem] md:h-32 lg:h-36"
            }`}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.isExternal ? (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                  useDarkText ? "text-foreground/80 hover:text-foreground hover:bg-accent" : "text-primary-foreground hover:text-primary-foreground text-shadow-header"
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                  useDarkText ? "text-foreground/80 hover:text-foreground hover:bg-accent" : "text-primary-foreground hover:text-primary-foreground text-shadow-header"
                }`}
              >
                {link.label}
              </Link>
            )
          )}

          <Link
            to="/maintenance"
            className="px-4 py-2 rounded-full text-sm font-bold transition-colors bg-secondary/80 text-secondary-foreground hover:bg-secondary"
          >
            ☀️ Valley Club
          </Link>
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:+16233014595" className={`flex items-center gap-1 font-bold text-sm ${useDarkText ? "text-primary" : "text-primary-foreground text-shadow-header"}`}>
            <Phone className={`w-4 h-4 ${useDarkText ? "" : "icon-shadow-header"}`} />
            (623) 301-4595
          </a>
        </div>

        {/* Mobile right side */}
        <div className="lg:hidden flex items-center gap-2">
          <a
            href="tel:+16233014595"
            className={`p-2 transition-colors ${useDarkText ? "text-primary" : "text-primary-foreground"}`}
            aria-label="Call us"
          >
            <Phone className={`w-5 h-5 ${useDarkText ? "" : "icon-shadow-header"}`} />
          </a>

          <button
            className={`p-2 transition-colors ${useDarkText ? "text-foreground" : "text-primary-foreground"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <X className={`w-6 h-6 ${useDarkText ? "" : "icon-shadow-header"}`} />
            ) : (
              <Menu className={`w-6 h-6 ${useDarkText ? "" : "icon-shadow-header"}`} />
            )}
          </button>
        </div>

      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-card border-t border-border shadow-2xl max-h-[calc(100vh-6rem)] overflow-y-auto">
          <nav className="container py-5 flex flex-col">
            {/* Featured: Valley Club */}
            <Link
              to="/maintenance"
              onClick={closeMenuForNavigation}
              className="group relative overflow-hidden rounded-2xl p-4 mb-5 bg-gradient-to-br from-secondary/90 to-secondary text-secondary-foreground shadow-fun flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl" aria-hidden>☀️</span>
                <div className="flex flex-col">
                  <span className="font-bold text-base leading-tight">Valley Club</span>
                  <span className="text-xs opacity-80 font-medium">Maintenance Memberships</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 opacity-70 group-hover:translate-x-1 transition-transform" />
            </Link>

            {/* Section label */}
            {mobileNavGroups.map((group) => (
              <div key={group.label} className="mb-3">
                <p className="px-2 mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
                <div className="flex flex-col gap-0.5">
                  {group.items.map((link) => {
                    const Icon = link.icon;
                    const content = (
                      <>
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Icon className="w-4.5 h-4.5" strokeWidth={2.25} />
                        </span>
                        <span className="flex-1 font-semibold text-foreground">{link.label}</span>
                        {link.isExternal && (
                          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/60" aria-hidden />
                        )}
                      </>
                    );
                     return link.isExternal ? (
                       <a
                         key={link.href}
                         href={link.href}
                         target="_blank"
                         rel="noopener noreferrer"
                         onClick={() => setMenuOpen(false)}
                         className="group flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-accent transition-colors"
                       >
                         {content}
                       </a>
                     ) : (
                       <Link
                         key={link.href}
                         to={link.href}
                         onClick={closeMenuForNavigation}
                         className="group flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-accent transition-colors"
                       >
                        {content}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Contact CTAs */}
            <div className="mt-3 pt-4 border-t border-border flex flex-col gap-2.5">
              <div className="flex gap-2">
                <Button variant="hero" size="lg" className="flex-1" asChild>
                  <Link to="/instant-quote" onClick={closeMenuForNavigation}>Instant Quote</Link>
                </Button>
                <Button variant="hero" size="lg" className="flex-1" asChild>
                  <Link to="/book" onClick={closeMenuForNavigation}>Book Estimate</Link>
                </Button>
              </div>
              <a
                href="tel:+16233014595"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-primary text-primary font-bold hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Phone className="w-4 h-4" />
                (623) 301-4595
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
